import React, { useState, useEffect } from "react";
import { useTheme } from "../../context/ThemeContext";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSignInAlt,
    faUserPlus,
    faBars,
    faTimes,
    faSignOutAlt,
    faSearch,
    faMoon,
    faLightbulb,
} from "@fortawesome/free-solid-svg-icons";
import logo1 from "../../assets/Images/logo1.png";
import logo2 from "../../assets/Images/logo2.png";
import tools from "../ToolCard/data/tools";

const Header = () => {
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(
        !!localStorage.getItem("token")
    );
    const { darkMode, toggleDarkMode } = useTheme();
    const [searchActive, setSearchActive] = useState(false);
    const [mobileSearchVisible, setMobileSearchVisible] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [showSearchResults, setShowSearchResults] = useState(false);

    useEffect(() => {
        setIsLoggedIn(!!localStorage.getItem("token"));
    }, []);

    useEffect(() => {
        if (searchQuery.trim()) {
            const results = tools.filter(tool => 
                tool.title.toLowerCase().includes(searchQuery.toLowerCase())
            ).slice(0, 5); // Limit to 5 results
            setSearchResults(results);
            setShowSearchResults(true);
        } else {
            setSearchResults([]);
            setShowSearchResults(false);
        }
    }, [searchQuery]);

    const handleHomeClick = () => {
        navigate("/");
    };

    const handleAuthClick = () => {
        navigate("/log-in");
    };

    const handleSignOutClick = () => {
        localStorage.removeItem("token");
        setIsLoggedIn(false);
        navigate("/");
    };

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        if (!isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
    };

    const handleSearchToggle = () => {
        setSearchActive(!searchActive);
        if (!searchActive) {
            setSearchQuery("");
            setMobileSearchVisible(false);
        }
    };

    const handleMobileSearchToggle = () => {
        setMobileSearchVisible(!mobileSearchVisible);
        if (mobileSearchVisible) {
            setSearchQuery("");
            setShowSearchResults(false);
        } else {
            // Close menu if open when opening search
            setIsMenuOpen(false);
            document.body.style.overflow = '';
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchResults.length > 0) {
            navigate(`/tool/${searchResults[0].path}`);
            setSearchQuery("");
            setShowSearchResults(false);
            setMobileSearchVisible(false);
        }
    };

    const handleToolSelect = (toolPath) => {
        navigate(`/tool/${toolPath}`);
        setSearchQuery("");
        setShowSearchResults(false);
        setSearchActive(false);
        setMobileSearchVisible(false);
    };

    return (
        <>
            <nav className="main-nav dark:text-white sticky top-0 z-50 w-full py-3 border-b border-gray-200 dark:border-gray-700 backdrop-blur-md bg-white/90 dark:bg-gray-900/90 shadow-sm  ">
                <div className="container mx-auto px-2 md:px-1 flex justify-between items-center">
                    <div className="flex items-center">
                        <div
                            className="flex items-center cursor-pointer"
                            onClick={handleHomeClick}
                        >
                            <img
                                src={logo1}
                                alt="Logo for Light Mode"
                                className="block dark:hidden h-6 md:h-12 w-auto"
                            />
                            <img
                                src={logo2}
                                alt="Logo for Dark Mode"
                                className="hidden dark:block h-6 md:h-12 w-auto"
                            />
                        </div>
                    </div>

                    <div className="flex items-center space-x-4">
                        {/* Mobile Search Button - Only visible when mobile search is not visible */}
                        {!mobileSearchVisible && (
                            <button
                                onClick={handleMobileSearchToggle}
                                className="md:hidden text-gray-600 dark:text-gray-300 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                aria-label="Search"
                            >
                                <FontAwesomeIcon icon={faSearch} size="sm" />
                            </button>
                        )}

                        {/* Desktop Search Field */}
                        <div className={`hidden md:block ${searchActive ? "w-48" : "w-0"}`}>
                            <form onSubmit={handleSearch} className="relative">
                                <input
                                    type="text"
                                    placeholder="Search tools..."
                                    className={`nav-search w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:border-indigo-500 dark:focus:border-indigo-400 dark:bg-gray-800 dark:text-white text-sm transition-all duration-300 ${searchActive ? 'opacity-100' : 'opacity-0'}`}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onFocus={() => setShowSearchResults(true)}
                                    onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                                />
                                <button
                                    type="submit"
                                    className={`absolute right-3 top-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-all duration-300 ${searchActive ? 'opacity-100' : 'opacity-0'}`}
                                    aria-label="Submit search"
                                >
                                    <FontAwesomeIcon icon={faSearch} size="sm" />
                                </button>
                            </form>
                            
                            {/* Search Results Dropdown */}
                            {showSearchResults && searchResults.length > 0 && (
                                <div className="absolute w-48 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                                    {searchResults.map((tool) => (
                                        <button
                                            key={tool.id}
                                            onClick={() => handleToolSelect(tool.path)}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0 transition-colors"
                                        >
                                            <div className="text-lg" style={{ color: tool.iconColor }}>
                                                {tool.icon}
                                            </div>
                                            <span className="font-medium text-gray-900 dark:text-white">
                                                {tool.title}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Desktop Search Toggle Button */}
                        {!searchActive && (
                            <button
                                onClick={handleSearchToggle}
                                className="hidden md:flex text-gray-600 dark:text-gray-300 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                aria-label="Toggle search"
                            >
                                <FontAwesomeIcon icon={faSearch} size="sm" />
                            </button>
                        )}

                        {/* Dark Mode Toggle */}
                        <button
                            onClick={toggleDarkMode}
                            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                            aria-label="Toggle dark mode"
                        >
                            <FontAwesomeIcon
                                icon={darkMode ? faLightbulb : faMoon}
                                className={`text-gray-600 dark:text-gray-300 transition-colors ${
                                    darkMode ? "text-yellow-400" : ""
                                }`}
                            />
                        </button>

                        {/* Desktop Auth Buttons */}
                        <div className="hidden md:flex items-center md:pr-1">
                            {!isLoggedIn ? (
                                <button
                                    onClick={handleAuthClick}
                                    className="bg-[#74D19C] hover:bg-[#74D19C] text-white px-4 py-2 rounded-lg font-medium transition text-sm flex items-center dark:bg-[#74D19C] dark:hover:bg-[#74D19C]"
                                >
                                    <FontAwesomeIcon
                                        icon={faSignInAlt}
                                        className="mr-1"
                                    />{" "}
                                    Login / Register
                                </button>
                            ) : (
                                <button
                                    onClick={handleSignOutClick}
                                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition text-sm dark:bg-red-700 dark:hover:bg-red-800"
                                >
                                    <FontAwesomeIcon
                                        icon={faSignOutAlt}
                                        className="mr-1"
                                    />{" "}
                                    Sign Out
                                </button>
                            )}
                        </div>

                        {/* Mobile Menu Button - Only visible when mobile search is not visible */}
                        {!mobileSearchVisible && (
                            <button
                                className="md:hidden text-gray-700 dark:text-gray-300 focus:outline-none p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                onClick={toggleMenu}
                                aria-label="Toggle menu"
                            >
                                <FontAwesomeIcon
                                    icon={isMenuOpen ? faTimes : faBars}
                                    size="sm"
                                />
                            </button>
                        )}
                    </div>
                </div>

                {/* Mobile Search Field - Full width when active */}
                {mobileSearchVisible && (
                    <div className="md:hidden px-4 py-2 bg-white dark:bg-gray-800 border-t border-b border-gray-200 dark:border-gray-700">
                        <form onSubmit={handleSearch} className="relative">
                            <div className="flex items-center">
                                <button
                                    type="button"
                                    onClick={handleMobileSearchToggle}
                                    className="text-gray-500 dark:text-gray-400 mr-2"
                                    aria-label="Close search"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                                <input
                                    type="text"
                                    placeholder="Search tools..."
                                    className="nav-search flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:border-indigo-500 dark:focus:border-indigo-400 dark:bg-gray-800 dark:text-white text-sm"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onFocus={() => setShowSearchResults(true)}
                                    autoFocus
                                />
                                <button
                                    type="submit"
                                    className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                    aria-label="Submit search"
                                >
                                    <FontAwesomeIcon icon={faSearch} size="sm" />
                                </button>
                            </div>
                        </form>

                        {/* Mobile Search Results Dropdown */}
                        {showSearchResults && searchResults.length > 0 && (
                            <div className="absolute left-0 right-0 mt-1 mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                                {searchResults.map((tool) => (
                                    <button
                                        key={tool.id}
                                        onClick={() => handleToolSelect(tool.path)}
                                        className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0 transition-colors"
                                    >
                                        <div className="text-lg" style={{ color: tool.iconColor }}>
                                            {tool.icon}
                                        </div>
                                        <span className="font-medium text-gray-900 dark:text-white">
                                            {tool.title}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </nav>

            {/* Mobile Menu - Similar to the HTML example */}
            {isMenuOpen && (
                <div 
                    className="mobile-menu fixed inset-0 bg-gray-900 bg-opacity-90 z-40 overflow-y-auto transition-transform duration-300 ease-in-out"
                    style={{ transform: isMenuOpen ? 'translateX(0)' : 'translateX(100%)' }}
                >
                    <div className="flex justify-end p-4">
                        <button 
                            onClick={toggleMenu}
                            className="p-2 text-white"
                        >
                            <FontAwesomeIcon icon={faTimes} className="text-2xl" />
                        </button>
                    </div>
                    <div className="flex flex-col items-center justify-center h-full px-4">
                        <div className="w-full max-w-sm space-y-4">
                            {!isLoggedIn ? (
                                <>
                                    <button
                                        onClick={() => {
                                            handleAuthClick();
                                            toggleMenu();
                                        }}
                                        className="w-full bg-white text-green-600 px-4 py-3 rounded-lg font-medium hover:bg-gray-100 transition flex items-center justify-center"
                                    >
                                        <FontAwesomeIcon icon={faSignInAlt} className="mr-2" />
                                        Sign In
                                    </button>
                                    <button
                                        onClick={() => {
                                            handleAuthClick();
                                            toggleMenu();
                                        }}
                                        className="w-full bg-green-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-green-700 transition flex items-center justify-center"
                                    >
                                        <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
                                        Register
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={() => {
                                        handleSignOutClick();
                                        toggleMenu();
                                    }}
                                    className="w-full bg-red-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-red-700 transition flex items-center justify-center"
                                >
                                    <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
                                    Sign Out
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    toggleDarkMode();
                                    toggleMenu();
                                }}
                                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg font-medium hover:bg-gray-700 transition flex items-center justify-center"
                            >
                                <FontAwesomeIcon 
                                    icon={darkMode ? faLightbulb : faMoon} 
                                    className="mr-2" 
                                />
                                Toggle Dark Mode
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header;